import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Target, Search, Repeat } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { IMAGES, CONTACT } from "@/data";

const values = [
  { icon: Target, title: "Precision over volume", body: "We don't spray and pray. Every contact is a decision maker who can actually award you work." },
  { icon: Search, title: "Research first", body: "We know who runs the buildings, manages the facilities, and signs the contracts before we ever reach out." },
  { icon: Repeat, title: "Relentless follow-up", body: "Deals live in the follow-up. We keep every opportunity warm until it becomes a signed contract." },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div data-testid="about-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 md:pt-52 pb-24 grid md:grid-cols-12 gap-14 items-end">
        <div className="md:col-span-7">
          <Reveal>
            <div className="overline text-gold mb-8 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" /> About
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
              Meet <span className="italic text-gold">Marcus <br className="hidden md:block" />Montgomery.</span>
            </h1>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="md:col-span-4 md:col-start-9">
          <p className="text-white/60 text-lg leading-relaxed">
            A consultant obsessed with one outcome — helping contractors and service providers win more commercial contracts, predictably.
          </p>
        </Reveal>
      </section>

      <section ref={ref} className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-28 grid md:grid-cols-12 gap-14 items-center">
        <div className="md:col-span-6 relative">
          <div className="relative overflow-hidden aspect-[4/3] border border-white/10">
            <motion.img style={{ y, scale: 1.14 }} src={IMAGES.marcus} alt="Marcus Montgomery" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050507]/50 to-transparent" />
          </div>
          <div className="absolute -top-6 -right-6 bg-gold text-black p-6 hidden md:block">
            <div className="font-serif text-3xl leading-none">Marcus M.</div>
            <div className="overline mt-2">Founder</div>
          </div>
        </div>

        <div className="md:col-span-6 md:col-start-7 space-y-6 text-white/70 text-lg leading-relaxed">
          <Reveal>
            <p>
              I built <span className="text-white">{CONTACT.brand}</span> after years spent on the other side of the table — closing commercial contracts as the Sales &amp; Marketing Director for companies across the commercial cleaning, seal coating, and total facility maintenance industries.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>
              In those roles I won and managed major accounts including <span className="text-white">Idaho Power facilities, CenturyLink facilities, Albertsons College, Valley Regional Transit</span>, and many more. I know exactly how commercial buyers think, what makes them say yes, and what it takes to keep them for the long haul.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p>
              That experience is your advantage. My Commercial Contract Acquisition Service closes the gap most contractors face — I identify the opportunities, research the decision makers, and get you in direct conversation with property managers, facility managers, business owners, and HOA managers.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p>
              Paired with modern lead generation systems and AI automations, it becomes a machine that generates bid opportunities, follows up automatically, and builds long-term client loyalty. With this body of knowledge, I'm uniquely qualified to help you procure more long-term, quality commercial contracts.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="values" className="bg-[#0F0F13] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28">
          <Reveal>
            <div className="overline text-gold mb-6">How I work</div>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-16 max-w-3xl">
              A few things I refuse to <span className="italic text-gold">compromise on</span>.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.12} className="h-full">
                <div className="h-full bg-[#0F0F13] p-10">
                  <v.icon className="text-gold mb-8" size={28} strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">{v.title}</h3>
                  <p className="text-white/60 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-10 max-w-3xl mx-auto">
            Let's talk about your <span className="italic text-gold">commercial goals</span>.
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="group inline-flex items-center gap-3 bg-gold text-black overline px-10 py-5 hover:bg-[#F3E5AB] transition-colors"
          >
            Book a Free Consultation
            <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
